import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LogisticsPausedBanner from "@/components/LogisticsPausedBanner";
import { TermsDialog } from "@/components/TermsDialog";
import { BookingRewards } from "@/components/BookingRewards";
import { usePublicSettings } from "@/lib/settings";
import { api, fileUrl } from "@/lib/api";
import { SERVICE_TYPES, URGENCY_LEVELS, FEATURE_IMG_TYRE } from "@/lib/constants";
import { Upload, X, Loader2, Camera, ArrowRight, ShieldCheck } from "lucide-react";

const initialForm = {
  full_name: "",
  phone: "",
  email: "",
  scooter_brand: "",
  scooter_model: "",
  issue_description: "",
  issue_started: "",
  is_rideable: "yes",
  service_type: "",
  preferred_dropoff_date: "",
  preferred_dropoff_time: "",
  urgency: "medium",
  pickup_required: false,
  dropoff_required: false,
  service_address: "",
};

const Field = ({ label, htmlFor, children, required, hint }) => (
  <div>
    <label htmlFor={htmlFor} className="label-mono mb-2 inline-block">
      {label}
      {required && <span className="text-spark-teal ml-1">*</span>}
    </label>
    {children}
    {hint && <p className="text-xs text-spark-slate mt-1.5">{hint}</p>}
  </div>
);

const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const formatTime = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const displayH = ((h % 12) || 12);
  return `${displayH}:${String(m).padStart(2, "0")}${period}`;
};

const summariseHours = (hours) => {
  if (!hours) return "";
  const groups = [];
  DAYS_ORDER.forEach((k) => {
    const d = hours[k];
    if (!d?.open) return;
    const range = `${formatTime(d.open_time)} – ${formatTime(d.close_time)}`;
    const last = groups[groups.length - 1];
    if (last && last.range === range) last.days.push(k);
    else groups.push({ range, days: [k] });
  });
  if (groups.length === 0) return "Closed all week — check back soon.";
  return groups.map((g) => `${g.days.map((k) => DAY_LABEL[k]).join(" · ")}, ${g.range}`).join(" | ");
};

const DAYS_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABEL = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
const DAY_FULL = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday" };

const Booking = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]); // [{file, uploading, media?}]
  const [submitting, setSubmitting] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [usePoints, setUsePoints] = useState(false);
  const [referralCode, setReferralCode] = useState(() => (new URLSearchParams(window.location.search).get("ref") || "").toUpperCase());
  const [hours, setHours] = useState(null);       // { monday: {open, open_time, close_time}, ... }
  const [closures, setClosures] = useState([]);   // [{ date: 'YYYY-MM-DD', label }]
  const publicSettings = usePublicSettings();
  const logisticsAvailable = publicSettings?.logistics_available !== false;

  // If the owner pauses logistics after a customer has already ticked a box
  // (stale page, slow network), silently clear those selections so the submit
  // can't fail at the backend guard.
  useEffect(() => {
    if (!logisticsAvailable && (form.pickup_required || form.dropoff_required)) {
      setForm((f) => ({ ...f, pickup_required: false, dropoff_required: false, service_address: "" }));
    }
  }, [logisticsAvailable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/settings/public").then((res) => {
      setHours(res.data?.business_hours || null);
      setClosures(res.data?.holiday_closures || []);
    }).catch(() => { /* keep permissive defaults */ });
  }, []);

  // Map: date string (YYYY-MM-DD) → closure label, for quick lookup
  const closureMap = useMemo(() => {
    const m = {};
    (closures || []).forEach((c) => { if (c?.date) m[c.date] = c.label || "closed"; });
    return m;
  }, [closures]);

  // Look up hours for the currently-selected date to drive min/max on the time picker
  const selectedDayHours = useMemo(() => {
    if (!hours || !form.preferred_dropoff_date) return null;
    const d = new Date(`${form.preferred_dropoff_date}T12:00:00`);
    const key = WEEKDAY_KEYS[d.getDay()];
    return hours[key] || null;
  }, [hours, form.preferred_dropoff_date]);

  const hoursSummary = useMemo(() => summariseHours(hours), [hours]);
  const closedDaySummary = useMemo(() => {
    if (!hours) return "";
    const closed = DAYS_ORDER.filter((k) => hours[k] && !hours[k].open).map((k) => DAY_LABEL[k]);
    return closed.length ? `Closed ${closed.join(", ")}.` : "";
  }, [hours]);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList);
    const queued = arr.map((f) => ({ file: f, uploading: true, media: null, error: null }));
    setFiles((s) => [...s, ...queued]);

    for (const item of queued) {
      try {
        const fd = new FormData();
        fd.append("file", item.file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((s) => s.map((it) => (it.file === item.file ? { ...it, uploading: false, media: res.data } : it)));
      } catch (err) {
        setFiles((s) =>
          s.map((it) =>
            it.file === item.file ? { ...it, uploading: false, error: err.response?.data?.detail || "Upload failed" } : it
          )
        );
        toast.error(`Upload failed: ${item.file.name}`);
      }
    }
  };

  const removeFile = (file) => setFiles((s) => s.filter((it) => it.file !== file));

  const validate = () => {
    const required = [
      "full_name", "phone", "email", "scooter_brand", "scooter_model",
      "issue_description", "issue_started", "service_type",
      "preferred_dropoff_date", "preferred_dropoff_time",
    ];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in: ${k.replace(/_/g, " ")}`);
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    // Enforce working hours dynamically from settings
    const dropoff = new Date(`${form.preferred_dropoff_date}T${form.preferred_dropoff_time}`);
    if (Number.isNaN(dropoff.getTime())) {
      toast.error("Drop-off date/time is invalid");
      return false;
    }
    if (closureMap[form.preferred_dropoff_date]) {
      toast.error(`We're closed that day — ${closureMap[form.preferred_dropoff_date]}. Please pick another date.`);
      return false;
    }
    if (hours) {
      const key = WEEKDAY_KEYS[dropoff.getDay()];
      const day = hours[key];
      if (!day?.open) {
        toast.error(`We're closed on ${DAY_FULL[key]}s — please pick a day we're open.`);
        return false;
      }
      if (form.preferred_dropoff_time < day.open_time || form.preferred_dropoff_time > day.close_time) {
        toast.error(`On ${DAY_FULL[key]}s we're open ${formatTime(day.open_time)} – ${formatTime(day.close_time)}. Please pick a time in that window.`);
        return false;
      }
    }
    if ((form.pickup_required || form.dropoff_required) && !form.service_address.trim()) {
      toast.error("Please add your address for pickup/drop-off");
      return false;
    }
    if (!termsAgreed) {
      toast.error("Please read and agree to the Terms & Conditions");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (files.some((f) => f.uploading)) {
      toast.error("Please wait for uploads to finish");
      return;
    }
    setSubmitting(true);
    try {
      const dropoff = new Date(`${form.preferred_dropoff_date}T${form.preferred_dropoff_time}`);
      const payload = {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        scooter_brand: form.scooter_brand,
        scooter_model: form.scooter_model,
        issue_description: form.issue_description,
        issue_started: form.issue_started,
        is_rideable: form.is_rideable === "yes",
        service_type: form.service_type,
        preferred_dropoff_at: dropoff.toISOString(),
        urgency: form.urgency,
        media: files.filter((f) => f.media).map((f) => f.media),
        quote_items: [],
        pickup_required: form.pickup_required,
        dropoff_required: form.dropoff_required,
        service_address: (form.pickup_required || form.dropoff_required) ? form.service_address.trim() : null,
        terms_accepted: termsAgreed,
        use_points: usePoints,
        referral_code: referralCode.trim() || null,
      };
      const res = await api.post("/bookings", payload);
      toast.success("Booking received");
      navigate(`/confirmation/${res.data.ref_number}`, { state: { job: res.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit booking");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <LogisticsPausedBanner />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow" data-testid="booking-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="label-mono text-spark-yellow mb-2">Step 1 of 1 · ~90 seconds</p>
            <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">
              Book a Repair
            </h1>
            <p className="mt-4 text-slate-300 max-w-xl text-base">
              Tell us about the scooter and what's not working. Add photos or video if you can — it helps us prep parts before you arrive.
            </p>
          </div>
          <div className="lg:col-span-4 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="size-4 text-spark-yellow" />
            <span>Your details are private and only used to service your repair.</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-12 gap-8">
        <form className="lg:col-span-8 space-y-px bg-spark-line border border-spark-line rounded-md overflow-hidden" onSubmit={handleSubmit} data-testid="booking-form">
          {/* Contact */}
          <section className="bg-white p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-1">01 Contact</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-6">Who are you?</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" htmlFor="full_name" required>
                <input id="full_name" className="input-style" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="input-full-name" />
              </Field>
              <Field label="Phone" htmlFor="phone" required>
                <input id="phone" className="input-style" placeholder="021 123 4567" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="input-phone" />
              </Field>
              <Field label="Email" htmlFor="email" required hint="We'll send updates here">
                <input id="email" type="email" className="input-style" value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="input-email" />
              </Field>
            </div>
          </section>

          {/* Scooter */}
          <section className="bg-white p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-1">02 The Scooter</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-6">What are we fixing?</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Brand" htmlFor="scooter_brand" required>
                <input id="scooter_brand" className="input-style" placeholder="e.g. Xiaomi, Segway, Pride" value={form.scooter_brand} onChange={(e) => setField("scooter_brand", e.target.value)} data-testid="input-scooter-brand" />
              </Field>
              <Field label="Model" htmlFor="scooter_model" required>
                <input id="scooter_model" className="input-style" placeholder="e.g. Mi Pro 2" value={form.scooter_model} onChange={(e) => setField("scooter_model", e.target.value)} data-testid="input-scooter-model" />
              </Field>
              <Field label="Is it rideable?" htmlFor="is_rideable" required>
                <div className="grid grid-cols-2 gap-2">
                  {["yes", "no"].map((opt) => (
                    <label key={opt} className={`flex items-center justify-center px-4 py-2.5 rounded-md border cursor-pointer font-display uppercase tracking-wider text-sm font-bold transition ${form.is_rideable === opt ? "bg-spark-teal text-white border-spark-teal" : "bg-white text-spark-ink border-spark-line hover:border-spark-teal"}`}>
                      <input type="radio" name="is_rideable" value={opt} className="sr-only" checked={form.is_rideable === opt} onChange={() => setField("is_rideable", opt)} data-testid={`radio-rideable-${opt}`} />
                      {opt === "yes" ? "Yes — rideable" : "No — won't move"}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Service Type" htmlFor="service_type" required>
                <select id="service_type" className="input-style" value={form.service_type} onChange={(e) => setField("service_type", e.target.value)} data-testid="select-service-type">
                  <option value="">Select a service…</option>
                  {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* Issue */}
          <section className="bg-white p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-1">03 The Problem</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-6">What's going on?</h2>
            <div className="space-y-5">
              <Field label="Describe the issue" htmlFor="issue_description" required>
                <textarea id="issue_description" rows={5} className="input-style resize-none" placeholder="e.g. Front wheel wobbles at speed, brake lever feels loose, throttle cuts out after 5 minutes…" value={form.issue_description} onChange={(e) => setField("issue_description", e.target.value)} data-testid="textarea-issue" />
              </Field>
              <Field label="When did it start?" htmlFor="issue_started" required>
                <input id="issue_started" className="input-style" placeholder="e.g. 3 days ago, after I hit a kerb" value={form.issue_started} onChange={(e) => setField("issue_started", e.target.value)} data-testid="input-issue-started" />
              </Field>
            </div>
          </section>

          {/* Drop-off / Pickup time */}
          <section className="bg-white p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-1">04 {form.pickup_required ? "Pickup time" : "Drop-off"}</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-2">
              {form.pickup_required ? "When should we collect it?" : "When can you bring it?"}
            </h2>
            {hoursSummary ? (
              <p className="text-sm text-spark-slate mb-6">Workshop hours: <strong className="text-spark-ink">{hoursSummary}</strong>{closedDaySummary ? <> · {closedDaySummary}</> : null}</p>
            ) : (
              <p className="text-sm text-spark-slate mb-6">Loading workshop hours…</p>
            )}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Preferred date" htmlFor="preferred_dropoff_date" required hint={hoursSummary ? "Only days we're open can be selected." : ""}>
                <input
                  id="preferred_dropoff_date"
                  type="date"
                  min={today}
                  className="input-style"
                  value={form.preferred_dropoff_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) {
                      if (closureMap[v]) {
                        toast.error(`We're closed that day — ${closureMap[v]}. Please pick another date.`);
                        return;
                      }
                      if (hours) {
                        const d = new Date(`${v}T12:00:00`);
                        const key = WEEKDAY_KEYS[d.getDay()];
                        if (!hours[key]?.open) {
                          toast.error(`We're closed on ${DAY_FULL[key]}s — please pick a day we're open.`);
                          return;
                        }
                      }
                    }
                    setField("preferred_dropoff_date", v);
                    // Clear a now-invalid time if the newly selected day has a different range
                    if (form.preferred_dropoff_time && hours && v) {
                      const d = new Date(`${v}T12:00:00`);
                      const key = WEEKDAY_KEYS[d.getDay()];
                      const day = hours[key];
                      if (day?.open && (form.preferred_dropoff_time < day.open_time || form.preferred_dropoff_time > day.close_time)) {
                        setField("preferred_dropoff_time", "");
                      }
                    }
                  }}
                  data-testid="input-dropoff-date"
                />
              </Field>
              <Field
                label="Preferred time"
                htmlFor="preferred_dropoff_time"
                required
                hint={selectedDayHours?.open ? `Open ${formatTime(selectedDayHours.open_time)} – ${formatTime(selectedDayHours.close_time)} that day` : (form.preferred_dropoff_date ? "Closed that day — pick another date" : "Pick a date first")}
              >
                <input
                  id="preferred_dropoff_time"
                  type="time"
                  min={selectedDayHours?.open_time || "09:00"}
                  max={selectedDayHours?.close_time || "17:00"}
                  step="900"
                  disabled={!selectedDayHours?.open}
                  className="input-style disabled:opacity-60 disabled:cursor-not-allowed"
                  value={form.preferred_dropoff_time}
                  onChange={(e) => setField("preferred_dropoff_time", e.target.value)}
                  data-testid="input-dropoff-time"
                />
              </Field>
              <Field label="Urgency" htmlFor="urgency" required>
                <div className="grid grid-cols-3 gap-2">
                  {URGENCY_LEVELS.map((u) => (
                    <label key={u.value} className={`text-center px-2 py-2.5 rounded-md border cursor-pointer text-xs font-display uppercase tracking-wider font-bold transition ${form.urgency === u.value ? "bg-spark-teal-deep text-white border-spark-ink" : "bg-white text-spark-ink border-spark-line hover:border-spark-ink"}`}>
                      <input type="radio" name="urgency" className="sr-only" checked={form.urgency === u.value} onChange={() => setField("urgency", u.value)} data-testid={`radio-urgency-${u.value}`} />
                      {u.value}
                    </label>
                  ))}
                </div>
              </Field>
            </div>

            {/* Pickup / Return delivery logistics — hidden entirely when the owner has paused the service */}
            {logisticsAvailable && (
            <div className="mt-6 pt-6 border-t border-spark-line" data-testid="logistics-section">
              <p className="label-mono text-spark-teal mb-1">Pickup / Return (optional)</p>
              <h3 className="font-display uppercase tracking-tight text-lg font-bold mb-2">Can't make it in?</h3>
              <p className="text-sm text-spark-slate mb-4">We can collect from you and/or return it once it's ready. <strong className="text-spark-ink">$40 each way</strong> · $80 for both. Return time confirmed once the job is done.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition ${form.pickup_required ? "bg-spark-teal-light border-spark-teal" : "bg-white border-spark-line hover:border-spark-ink"}`} data-testid="opt-pickup">
                  <input type="checkbox" checked={form.pickup_required} onChange={(e) => setField("pickup_required", e.target.checked)} className="mt-0.5 size-4 accent-spark-teal" data-testid="check-pickup" />
                  <span className="flex-1 text-sm">
                    <strong className="block font-display uppercase tracking-tight text-spark-ink">Pickup from me</strong>
                    <span className="text-spark-slate text-xs">We collect your scooter from your address (your "drop-off time" above becomes the pickup time).</span>
                  </span>
                  <span className="font-display uppercase tracking-tight font-bold text-spark-ink text-sm">+$40</span>
                </label>
                <label className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition ${form.dropoff_required ? "bg-spark-teal-light border-spark-teal" : "bg-white border-spark-line hover:border-spark-ink"}`} data-testid="opt-dropoff">
                  <input type="checkbox" checked={form.dropoff_required} onChange={(e) => setField("dropoff_required", e.target.checked)} className="mt-0.5 size-4 accent-spark-teal" data-testid="check-dropoff" />
                  <span className="flex-1 text-sm">
                    <strong className="block font-display uppercase tracking-tight text-spark-ink">Return to me</strong>
                    <span className="text-spark-slate text-xs">We deliver your scooter back once it's ready (return time confirmed when the job is done).</span>
                  </span>
                  <span className="font-display uppercase tracking-tight font-bold text-spark-ink text-sm">+$40</span>
                </label>
              </div>
              {(form.pickup_required || form.dropoff_required) && (
                <div className="mt-4" data-testid="address-row">
                  <Field label="Address for pickup / return" htmlFor="service_address" required hint="Street, suburb, city — anything we need to find you.">
                    <textarea id="service_address" rows={2} className="input-style resize-none" placeholder="123 Example St, Newtown, Wellington" value={form.service_address} onChange={(e) => setField("service_address", e.target.value)} data-testid="input-address" />
                  </Field>
                  <p className="text-xs text-spark-teal-dark mt-2 font-display uppercase tracking-wider font-bold" data-testid="logistics-total">
                    Logistics total: ${(form.pickup_required ? 40 : 0) + (form.dropoff_required ? 40 : 0)}
                  </p>
                </div>
              )}
            </div>
            )}
          </section>

          {/* Media */}
          <section className="bg-white p-6 sm:p-8">
            <p className="label-mono text-spark-teal mb-1">06 Photos / Video</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold mb-2">Show us the damage</h2>
            <p className="text-sm text-spark-slate mb-5">Optional but recommended — up to 25MB per file.</p>

            <label className="block border-2 border-dashed border-spark-teal/60 hover:border-spark-teal bg-spark-teal-light/40 hover:bg-spark-teal-light rounded-md p-8 text-center cursor-pointer transition" data-testid="upload-dropzone">
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="upload-input"
              />
              <Camera className="size-8 mx-auto text-spark-teal mb-2" />
              <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Click to upload</p>
              <p className="text-xs text-spark-slate mt-1">Photos &amp; videos · multiple files supported</p>
            </label>

            {files.length > 0 && (
              <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="upload-preview-list">
                {files.map((it, i) => (
                  <li key={i} className="relative group rounded-md overflow-hidden border border-spark-line bg-white">
                    {it.file.type.startsWith("image/") ? (
                      <img src={URL.createObjectURL(it.file)} alt={it.file.name} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center bg-spark-teal-deep text-spark-yellow">
                        <Camera className="size-8" />
                      </div>
                    )}
                    <button type="button" onClick={() => removeFile(it.file)} className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white rounded-full p-1 shadow" data-testid={`remove-file-${i}`}>
                      <X className="size-3.5" />
                    </button>
                    <div className="absolute inset-x-0 bottom-0 bg-spark-teal-deep/85 text-white text-[10px] px-2 py-1 truncate">
                      {it.uploading ? (
                        <span className="flex items-center gap-1"><Loader2 className="size-3 animate-spin" /> Uploading…</span>
                      ) : it.error ? (
                        <span className="text-red-300">{it.error}</span>
                      ) : (
                        <span className="text-emerald-300">✓ Uploaded</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <BookingRewards
            email={form.email}
            phone={form.phone}
            usePoints={usePoints}
            setUsePoints={setUsePoints}
            referralCode={referralCode}
            setReferralCode={setReferralCode}
          />

          {/* Submit */}
          <section className="bg-spark-teal-deep text-white p-6 sm:p-8 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="terms-agree-label">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-0.5 size-4 accent-spark-yellow shrink-0"
                  data-testid="terms-agree-checkbox"
                />
                <span>
                  I have read and agree to the{" "}
                  <button type="button" onClick={() => setTermsOpen(true)} className="text-spark-yellow underline underline-offset-2 hover:text-white font-semibold" data-testid="terms-open-link">
                    Terms & Conditions
                  </button>
                  <span className="text-spark-yellow"> *</span>
                </span>
              </label>
              <p className="text-xs text-slate-400">We'll contact you on the details above to confirm and follow up.</p>
            </div>
            <button type="submit" disabled={submitting} className="btn-accent" data-testid="submit-booking-btn">
              {submitting ? (<><Loader2 className="size-4 animate-spin" /> Sending…</>) : (<>Submit Booking <ArrowRight className="size-4" /></>)}
            </button>
          </section>
        </form>
        <TermsDialog
          open={termsOpen}
          onOpenChange={setTermsOpen}
          onAgree={() => { setTermsAgreed(true); setTermsOpen(false); }}
        />

        {/* Side panel */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="surface-card overflow-hidden">
            <div className="aspect-[4/3]">
              <img src={FEATURE_IMG_TYRE} alt="Repair workshop" className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <p className="label-mono text-spark-teal mb-2">What happens next</p>
              <ol className="text-sm space-y-2 text-spark-slate">
                <li><span className="font-bold text-spark-ink">1.</span> You get a unique reference number.</li>
                <li><span className="font-bold text-spark-ink">2.</span> Use it on the tracker page to follow progress.</li>
                <li><span className="font-bold text-spark-ink">3.</span> We contact you to confirm and quote.</li>
              </ol>
            </div>
          </div>
          <div className="surface-card p-5">
            <p className="label-mono text-spark-teal mb-2">Already booked?</p>
            <p className="text-sm text-spark-slate mb-3">Track your existing repair using your reference number or email.</p>
            <Link to="/track" className="btn-outline-ink w-full" data-testid="aside-track-btn">Track a Job</Link>
          </div>
        </aside>
      </main>

      <Footer />
    </div>
  );
};

export default Booking;
